<template>
  <div v-loading="loading" class="nx-page nx-report-page kanban-monthly-trend-benchmark">
    <el-form :inline="true" class="kb-filter-bar" size="small" @submit.native.prevent>
      <el-form-item label="统计年度">
        <el-date-picker
          v-model="yearVal"
          type="year"
          value-format="yyyy"
          placeholder="选择年"
          style="width: 120px"
        />
      </el-form-item>
      <el-form-item label="业务类型">
        <el-select v-model="filters.businessType" clearable placeholder="全部" style="width: 130px">
          <el-option label="铁运入库" :value="1" />
          <el-option label="汽运出库" :value="2" />
          <el-option label="汽运入库" :value="3" />
        </el-select>
      </el-form-item>
      <el-form-item label="计划类型">
        <el-select v-model="filters.planType" clearable placeholder="全部" style="width: 120px">
          <el-option label="年计划" value="0" />
          <el-option label="月计划" value="1" />
          <el-option label="日计划" value="2" />
        </el-select>
      </el-form-item>
      <el-form-item label="标杆(%)">
        <el-input-number v-model="benchInput" :min="50" :max="100" :step="1" :controls="true" style="width: 110px" />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="loadData">查询</el-button>
      </el-form-item>
    </el-form>

    <div class="kb-kpi-row">
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">本年累计计划量（吨）</div>
        <div class="kb-kpi-value">{{ fmtTon(kpi.yearPlanTotal) }}</div>
      </div>
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">本年累计执行量（吨）</div>
        <div class="kb-kpi-value">{{ fmtTon(kpi.yearExecTotal) }}</div>
      </div>
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">年度综合执行率</div>
        <div class="kb-kpi-value" :class="{ 'is-warn': yearBelowBench }">
          {{ kpi.yearRatePercent != null ? kpi.yearRatePercent + '%' : '—' }}
        </div>
      </div>
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">达标月份 / 共 12 月</div>
        <div class="kb-kpi-value">
          {{ kpi.monthsMeetBenchmark != null ? kpi.monthsMeetBenchmark : 0 }} / 12
        </div>
      </div>
    </div>

    <div class="kb-charts-row">
      <div class="kb-chart-card">
        <div class="kb-chart-head">
          <span>月度计划量 vs 执行量（吨）</span>
        </div>
        <div ref="chartPlanExec" class="kb-chart-box" />
      </div>
      <div class="kb-chart-card">
        <div class="kb-chart-head">
          <span>月度执行率 vs 标杆（%）</span>
        </div>
        <div ref="chartRate" class="kb-chart-box kb-chart-box-sm" />
      </div>
    </div>

    <div class="kb-charts-row-triple">
      <div class="kb-chart-card">
        <div class="kb-chart-head">
          <span>分业务类型 · 月度执行量堆叠（吨）</span>
        </div>
        <div ref="chartStack" class="kb-chart-box" />
      </div>
    </div>

    <div class="kb-section">
      <div class="kb-section-head">
        <span>计划明细</span>
      </div>
      <el-table :data="planRows" border stripe size="small" max-height="420" style="width: 100%">
        <el-table-column prop="planNo" label="计划编号" min-width="130" show-overflow-tooltip />
        <el-table-column prop="businessTypeLabel" label="业务类型" width="100" />
        <el-table-column prop="custName" label="货主名称" min-width="110" show-overflow-tooltip />
        <el-table-column prop="cargoName" label="煤种名称" min-width="100" show-overflow-tooltip />
        <el-table-column prop="planTotalWeight" label="计划数量（吨）" width="120" align="right" :formatter="fmtTonCol" />
        <el-table-column prop="planExecuteWeight" label="计划执行进度（吨）" width="140" align="right" :formatter="fmtTonCol" />
        <el-table-column label="执行率" min-width="140">
          <template slot-scope="{ row }">
            <span>{{ row.executeRatePercent != null ? row.executeRatePercent + '%' : '—' }}</span>
            <div v-if="row.executeRatePercent != null" class="pct-bar">
              <i :style="{ width: barW(row.executeRatePercent) }" />
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import NxReportHeader from '@/components/nx/report/NxReportHeader.vue'
import { fetchMonthlyTrendBenchmark } from './api'

export default {
  name: 'KanbanMonthlyTrendBenchmark',
  components: { NxReportHeader },
  data() {
    const y = String(new Date().getFullYear())
    return {
      loading: false,
      yearVal: y,
      benchInput: 95,
      filters: {
        businessType: null,
        planType: null
      },
      result: null,
      chartInstances: []
    }
  },
  computed: {
    kpi() {
      const k = this.result && this.result.kpi
      return (
        k || {
          yearPlanTotal: null,
          yearExecTotal: null,
          yearRatePercent: null,
          monthsMeetBenchmark: 0,
          monthsWithPlan: 0
        }
      )
    },
    planRows() {
      return (this.result && this.result.planSampleRows) || []
    },
    yearBelowBench() {
      const r = this.kpi.yearRatePercent
      const b = this.result && this.result.benchmarkPercent != null ? Number(this.result.benchmarkPercent) : this.benchInput
      if (r == null) return false
      return Number(r) < b
    }
  },
  mounted() {
    this.loadData()
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    this.disposeCharts()
  },
  methods: {
    barW(pct) {
      const n = Math.min(100, Math.max(0, Number(pct) || 0))
      return n + '%'
    },
    fmtTon(v) {
      if (v == null || v === '') return '—'
      const n = Number(v)
      if (Number.isNaN(n)) return '—'
      return n.toLocaleString('zh-CN', { maximumFractionDigits: 2 })
    },
    fmtTonCol(row, col, val) {
      return this.fmtTon(val)
    },
    disposeCharts() {
      this.chartInstances.forEach((c) => c && c.dispose())
      this.chartInstances = []
    },
    onResize() {
      this.chartInstances.forEach((c) => c && c.resize())
    },
    async loadData() {
      const year = parseInt(this.yearVal, 10)
      const y = Number.isFinite(year) ? year : new Date().getFullYear()
      this.loading = true
      try {
        const body = {
          year: y,
          businessType: this.filters.businessType,
          planType: this.filters.planType || undefined,
          benchmarkPercent: this.benchInput,
          planTableLimit: 50
        }
        const data = await fetchMonthlyTrendBenchmark(body)
        this.result = data
        this.$nextTick(() => this.renderCharts())
      } catch (e) {
        this.$message.error((e && e.message) || '加载失败')
      } finally {
        this.loading = false
      }
    },
    renderCharts() {
      this.disposeCharts()
      if (!this.result) return
      const months = (this.result.monthlySeries || []).map((m) => m.monthLabel || m.month + '月')
      const planW = (this.result.monthlySeries || []).map((m) => Number(m.planTotal) || 0)
      const execW = (this.result.monthlySeries || []).map((m) => Number(m.execTotal) || 0)
      const rates = (this.result.monthlySeries || []).map((m) =>
        m.ratePercent != null ? Number(m.ratePercent) : null
      )
      const bench = Number(this.result.benchmarkPercent != null ? this.result.benchmarkPercent : this.benchInput)

      const c1 = echarts.init(this.$refs.chartPlanExec)
      c1.setOption({
        tooltip: { trigger: 'axis' },
        legend: { data: ['计划量', '执行量'], bottom: 0 },
        grid: { left: 52, right: 24, top: 24, bottom: 48 },
        xAxis: { type: 'category', data: months },
        yAxis: { type: 'value', name: '吨' },
        series: [
          { name: '计划量', type: 'bar', data: planW, itemStyle: { color: '#94a3b8', borderRadius: [4, 4, 0, 0] } },
          {
            name: '执行量',
            type: 'line',
            smooth: true,
            data: execW,
            lineStyle: { width: 3, color: '#0284c7' },
            itemStyle: { color: '#0284c7' }
          }
        ]
      })
      this.chartInstances.push(c1)

      const c2 = echarts.init(this.$refs.chartRate)
      c2.setOption({
        tooltip: {
          trigger: 'axis',
          formatter(arr) {
            const a = arr[0]
            const v = a.value
            const t = v != null && v !== '' ? Number(v).toFixed(1) + '%' : '—'
            return a.name + '<br/>' + a.marker + a.seriesName + ': ' + t
          }
        },
        legend: { data: ['月度执行率'], bottom: 0 },
        grid: { left: 48, right: 24, top: 28, bottom: 48 },
        xAxis: { type: 'category', data: months },
        yAxis: {
          type: 'value',
          name: '%',
          max: Math.max(100, bench + 10, ...rates.filter((x) => x != null).map((x) => Number(x)))
        },
        series: [
          {
            name: '月度执行率',
            type: 'line',
            smooth: true,
            data: rates,
            connectNulls: false,
            lineStyle: { width: 3, color: '#0284c7' },
            itemStyle: { color: '#0284c7' },
            markLine: {
              symbol: 'none',
              label: { formatter: '标杆 ' + bench + '%' },
              data: [{ yAxis: bench, lineStyle: { type: 'dashed', color: '#ea580c', width: 2 } }]
            }
          }
        ]
      })
      this.chartInstances.push(c2)

      const stack = this.result.stackedExecByBusiness || []
      const rail = stack.map((r) => Number(r.railInExec) || 0)
      const out = stack.map((r) => Number(r.truckOutExec) || 0)
      const inn = stack.map((r) => Number(r.truckInExec) || 0)
      const c3 = echarts.init(this.$refs.chartStack)
      c3.setOption({
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend: { data: ['铁运入库', '汽运出库', '汽运入库'], bottom: 0 },
        grid: { left: 48, right: 16, top: 16, bottom: 48 },
        xAxis: { type: 'category', data: months },
        yAxis: { type: 'value', name: '吨' },
        series: [
          { name: '铁运入库', type: 'bar', stack: 't', data: rail, itemStyle: { color: '#1d4ed8' } },
          { name: '汽运出库', type: 'bar', stack: 't', data: out, itemStyle: { color: '#ea580c' } },
          { name: '汽运入库', type: 'bar', stack: 't', data: inn, itemStyle: { color: '#059669' } }
        ]
      })
      this.chartInstances.push(c3)
    }
  }
}
</script>

<style lang="scss" scoped>
@import './index.scss';

.kb-header-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
</style>
